import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Shield } from 'lucide-react';
import { setupRecaptcha, clearPhoneAuthSession } from '../../services/firebase';

/**
 * Bottom sheet overlay containing reCAPTCHA verification
 * Slides up from the bottom when shown
 */
export default function RecaptchaBottomSheet({ isOpen, onClose, onVerified, onError }) {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [containerMounted, setContainerMounted] = useState(false);
  const hasInitialized = useRef(false);

  // Ref callback to detect when container is mounted
  const containerRefCallback = useCallback((node) => {
    if (node !== null) {
      console.log('[RecaptchaBottomSheet] Container mounted');
      setContainerMounted(true);
    }
  }, []);

  // Initialize reCAPTCHA when container is mounted
  useEffect(() => {
    if (isOpen && containerMounted && !hasInitialized.current) {
      initRecaptcha();
    }
  }, [isOpen, containerMounted]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      hasInitialized.current = false;
      setIsReady(false);
      setIsLoading(true);
      setError('');
      setContainerMounted(false);
    }
  }, [isOpen]);

  const initRecaptcha = async () => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    setIsLoading(true);
    setError('');

    try {
      // Small delay to ensure DOM is fully ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Clear any existing reCAPTCHA
      clearPhoneAuthSession();

      // Setup reCAPTCHA with callbacks
      await setupRecaptcha('recaptcha-sheet-container', {
        onVerified: (token) => {
          console.log('[RecaptchaBottomSheet] User completed verification');
          // User has verified - notify parent to proceed
          if (onVerified) {
            onVerified();
          }
        },
        onExpired: () => {
          console.log('[RecaptchaBottomSheet] Verification expired');
          setError('Verification expired. Please try again.');
        },
        onError: (err) => {
          console.error('[RecaptchaBottomSheet] Verification error:', err);
          setError('Verification failed. Please try again.');
        },
      });

      setIsReady(true);
      setIsLoading(false);
      console.log('[RecaptchaBottomSheet] reCAPTCHA widget rendered, waiting for user');
    } catch (err) {
      console.error('[RecaptchaBottomSheet] Failed to init reCAPTCHA:', err);
      setError('Failed to load verification. Please try again.');
      setIsLoading(false);
      if (onError) {
        onError(err);
      }
    }
  };

  const handleRetry = () => {
    hasInitialized.current = false;
    setContainerMounted(false);
    setError('');
    // Force remount by briefly setting containerMounted false
    setTimeout(() => {
      setContainerMounted(true);
    }, 100);
  };

  const handleClose = () => {
    clearPhoneAuthSession();
    hasInitialized.current = false;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={handleClose}
      />

      {/* Bottom Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl transform transition-transform duration-300 ease-out animate-slide-up"
        style={{ maxHeight: '80vh' }}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <Shield size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Security Check</h2>
              <p className="text-sm text-gray-500">Complete verification to continue</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-8 pb-[calc(2rem+env(safe-area-inset-bottom))]">
          {error ? (
            <div className="text-center">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <p className="text-red-600">{error}</p>
              </div>
              <button
                onClick={handleRetry}
                className="text-indigo-600 font-medium hover:underline"
              >
                Try again
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <p className="text-gray-600 text-center mb-6">
                Please complete the verification below to proceed.
              </p>

              {/* reCAPTCHA container - always rendered */}
              <div
                id="recaptcha-sheet-container"
                ref={containerRefCallback}
                className="flex justify-center min-h-[78px]"
              />

              {/* Loading overlay */}
              {isLoading && (
                <div className="flex flex-col items-center justify-center py-4">
                  <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3" />
                  <p className="text-gray-500 text-sm">Loading verification...</p>
                </div>
              )}

              {isReady && !isLoading && (
                <p className="text-sm text-gray-500 mt-4 text-center">
                  Check the box above to verify you're human
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
